import { useEffect, useRef, useState } from 'react';
import { resolveSeason, Season, SEASONS, SeasonId } from './calendar';
import { resetSeasonState } from './store';

/**
 * Anulación del calendario, para previsualizar una temporada sin esperar a la fecha.
 *
 * Prioridad (E07-F01, R2):
 *   1. `?season=<valor>` en la URL. Un valor distinto de `off` se guarda en
 *      `localStorage` (clave `medinterpret.season.force`) y sigue mandando en las
 *      cargas siguientes; `off` borra la clave y no se guarda.
 *   2. La clave de `localStorage`.
 *   3. `REACT_APP_SEASON_FORCE` del entorno (`.env.local`), el valor por defecto.
 *
 * Valores: `dia-de-muertos` | `navidad` (esa temporada sin mirar la fecha), `none`
 * (sin temporada), `YYYY-MM-DD` (la temporada del calendario para ese día) y cualquier
 * otra cosa cae al calendario para "hoy".
 *
 * Disponible en producción a propósito (E07-F01, P1): sólo afecta al navegador que la
 * usa, no toca datos ni a otros usuarios, y es la única forma de revisar Navidad en el
 * entorno real antes del 4 de noviembre. Sin anulación no se escribe nada.
 */
const STORAGE_KEY = 'medinterpret.season.force';

export const readForce = (): string | null => {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('season');
    if (fromUrl === 'off') {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    if (fromUrl) {
      window.localStorage.setItem(STORAGE_KEY, fromUrl);
      return fromUrl;
    }
    return window.localStorage.getItem(STORAGE_KEY) ?? process.env.REACT_APP_SEASON_FORCE ?? null;
  } catch {
    // localStorage puede lanzar (modo privado, permisos): manda sólo el entorno.
    return process.env.REACT_APP_SEASON_FORCE ?? null;
  }
};

/** La temporada activa en `now`, con la anulación por delante del calendario. */
export const resolveWithForce = (now: Date): Season | null => {
  const force = readForce();
  if (force === 'none') return null;
  if (force) {
    const byId = SEASONS.find((s) => s.id === (force as SeasonId));
    if (byId) return byId;
    // Una fecha (YYYY-MM-DD) simula "hoy" para ver qué toca ese día.
    const asDate = new Date(`${force}T12:00:00`);
    if (!Number.isNaN(asDate.getTime())) return resolveSeason(asDate);
  }
  return resolveSeason(now);
};

/** Milisegundos hasta un minuto después de la próxima medianoche local. */
const msUntilTomorrow = (now: Date): number => {
  const next = new Date(now);
  next.setDate(now.getDate() + 1);
  next.setHours(0, 1, 0, 0);
  return next.getTime() - now.getTime();
};

/**
 * La temporada activa, re-evaluada a las 00:01 de cada día y cada vez que la pestaña
 * vuelve a estar visible: el panel se queda abierto días con el polling, y quien tenga
 * la pestaña abierta el 3 de noviembre debe amanecer el 4 con Navidad sin recargar.
 *
 * Cuando el ID de la temporada resuelta cambia (también a/desde `null`), el estado
 * compartido vuelve a `default` (E07-F01, R3 / P8): si no, la variante `halloween` del
 * 3-nov sobreviviría al 4-nov con `data-variant` mintiendo y un clic en el regalo
 * dispararía con estado sucio. La comparación es por ID y POR INSTANCIA (`useRef`), no
 * contra el estado compartido: el hook vive en tres componentes (cabecera, barra,
 * overlays) y cada uno detecta el cruce por su cuenta; `resetSeasonState` es
 * idempotente, así que tres detecciones cuestan una sola emisión. El estado inicial no
 * reinicia nada: un montaje nuevo ya arranca en `default`.
 */
export const useSeason = (): Season | null => {
  const [season, setSeason] = useState<Season | null>(() => resolveWithForce(new Date()));
  const lastId = useRef<SeasonId | null>(season?.id ?? null);

  useEffect(() => {
    const refresh = () => {
      const next = resolveWithForce(new Date());
      const nextId = next?.id ?? null;
      if (nextId !== lastId.current) {
        lastId.current = nextId;
        resetSeasonState();
      }
      setSeason(next);
    };
    let timer = window.setTimeout(function tick() {
      refresh();
      timer = window.setTimeout(tick, msUntilTomorrow(new Date()));
    }, msUntilTomorrow(new Date()));
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  return season;
};
